<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('agents/getUIFlags');
const existingConversations = useMapGetter(
  'agentConversations/getConversations'
);
const searchQuery = ref('');

const otherAgents = computed(() =>
  (agents.value || []).filter(a => a.id !== currentUser.value?.id)
);

const filtered = computed(() => {
  const q = searchQuery.value.toLowerCase();
  if (!q) return otherAgents.value;
  return otherAgents.value.filter(a => a.name.toLowerCase().includes(q));
});

const groupedAgents = computed(() => {
  const online = filtered.value.filter(a => a.availability_status === 'online');
  const busy = filtered.value.filter(a => a.availability_status === 'busy');
  const offline = filtered.value.filter(
    a => a.availability_status !== 'online' && a.availability_status !== 'busy'
  );
  return [
    {
      key: 'online',
      label: t('AGENT_CHAT.MODAL.SECTION_ONLINE'),
      agents: online,
    },
    { key: 'busy', label: t('AGENT_CHAT.MODAL.SECTION_BUSY'), agents: busy },
    {
      key: 'offline',
      label: t('AGENT_CHAT.MODAL.SECTION_OFFLINE'),
      agents: offline,
    },
  ].filter(g => g.agents.length > 0);
});

const isLoading = computed(() => uiFlags.value?.isFetching);

onMounted(() => {
  store.dispatch('agents/get');
});

const navigateTo = convId => {
  router.push(
    frontendURL(`accounts/${route.params.accountId}/agent-chat/${convId}`)
  );
};

const startConversation = async agent => {
  // Se já existe conversa na store, navega direto sem chamar a API
  const uid = currentUser.value?.id;
  const existing = existingConversations.value?.find(c => {
    const otherId = c.initiator.id === uid ? c.participant.id : c.initiator.id;
    return otherId === agent.id;
  });

  if (existing) {
    navigateTo(existing.id);
    emit('close');
    return;
  }

  // Nova conversa: cria via API e navega
  try {
    const conv = await store.dispatch(
      'agentConversations/findOrCreate',
      agent.id
    );
    if (conv?.id) navigateTo(conv.id);
  } finally {
    emit('close');
  }
};

const roleLabel = agent =>
  agent.role === 'administrator'
    ? t('AGENT_CHAT.MODAL.ROLE_ADMINISTRATOR')
    : t('AGENT_CHAT.MODAL.ROLE_AGENT');
</script>

<template>
  <woot-modal show :on-close="() => emit('close')" size="small">
    <woot-modal-header
      :header-title="t('AGENT_CHAT.MODAL.TITLE')"
      :header-content="t('AGENT_CHAT.MODAL.DESCRIPTION')"
    />

    <div class="px-6 pt-4 pb-6 flex flex-col gap-3">
      <!-- Search -->
      <div class="relative">
        <span
          class="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 i-lucide-search size-4 text-n-slate-10 pointer-events-none"
        />
        <input
          v-model="searchQuery"
          type="search"
          autofocus
          class="reset-base w-full h-9 ltr:pl-9 ltr:pr-3 rtl:pr-9 rtl:pl-3 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-blue-11"
          :placeholder="t('AGENT_CHAT.SEARCH_AGENT')"
        />
      </div>

      <!-- Agent list -->
      <div class="max-h-72 overflow-y-auto flex flex-col">
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-8 gap-2 text-sm text-n-slate-10"
        >
          <span class="i-lucide-loader-circle size-4 animate-spin" />
          {{ t('AGENT_CHAT.MODAL.LOADING') }}
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!filtered.length"
          class="flex flex-col items-center justify-center py-8 gap-2 text-sm text-n-slate-10"
        >
          <span class="i-lucide-user-x size-8 text-n-slate-8" />
          {{ t('AGENT_CHAT.NO_AGENTS_FOUND') }}
        </div>

        <!-- Grouped list -->
        <template v-else>
          <div
            v-for="group in groupedAgents"
            :key="group.key"
            class="flex flex-col"
          >
            <!-- Section header -->
            <p
              class="px-2 pt-2 pb-1 text-xs font-medium text-n-slate-10 uppercase tracking-wide sticky top-0 bg-n-alpha-3 backdrop-blur-sm z-10"
            >
              {{ group.label }}
            </p>

            <!-- Agents -->
            <button
              v-for="agent in group.agents"
              :key="agent.id"
              class="flex items-center gap-3 w-full px-2 py-2.5 rounded-lg text-left hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-11"
              @click="startConversation(agent)"
            >
              <Avatar
                :src="agent.avatar_url"
                :name="agent.name"
                :size="36"
                :status="agent.availability_status"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="text-sm font-medium text-n-slate-12 truncate">{{
                    agent.name
                  }}</span>
                  <span
                    class="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-n-alpha-2 dark:bg-n-solid-3 text-n-slate-10"
                    >{{ roleLabel(agent) }}</span
                  >
                </div>
                <p
                  v-if="agent.email"
                  class="text-xs text-n-slate-10 truncate mt-0.5"
                >
                  {{ agent.email }}
                </p>
              </div>
            </button>
          </div>
        </template>
      </div>
    </div>
  </woot-modal>
</template>
